import {ChangeDetectionStrategy, Component, computed, inject, input} from '@angular/core';
import {FormArray, FormControl, FormGroup, FormGroupDirective} from '@angular/forms';
import {HarbourDuesBerthVisitForm, HarbourDuesGoodsItemForm, UnitOfMass} from '../../../../../types';
import {HarbourDuesService} from '../../../../../services/harbour-dues.service';
import {
  HarbourDuesBerthVisit,
  HarbourDuesRotterdamLookupCargoType
} from '@portbase/bezoekschip-service-typescriptmodels';
import {toSignal} from '@angular/core/rxjs-interop';
import _ from 'lodash';
import {AlertSeverity} from '../../../../../../../common/alert/alert.component';

@Component({
  selector: 'app-berth-visit-cargo-rotterdam',
  templateUrl: './berth-visit-cargo-rotterdam.component.html',
  styleUrl: './berth-visit-cargo-rotterdam.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class BerthVisitCargoRotterdamComponent {
  protected readonly UnitOfMass = UnitOfMass;
  protected readonly AlertSeverity = AlertSeverity;

  readonly berthVisitNumber = input.required<number>();
  readonly mode = input.required<CargoComponentMode>();
  readonly lookupCargoTypes = input<HarbourDuesRotterdamLookupCargoType[]>();

  private readonly form = inject(FormGroupDirective);
  private readonly harbourDuesService = inject(HarbourDuesService);

  protected berthVisitGroup?: FormControl<HarbourDuesBerthVisitForm>;
  protected goodsGroups?: FormArray<FormGroup<HarbourDuesGoodsItemForm>>;

  private readonly formValues = toSignal<{berthVisits: HarbourDuesBerthVisit[]}>(
    this.form.control.valueChanges,
    { initialValue: this.form.control.value }
  );

  protected readonly cargoTypes = computed(
    () => this.lookupCargoTypes()?.map(cargoType => cargoType.cargoType) as string[] || []
  );

  protected readonly goods = computed(() => {
    const berthVisit = this.formValues()?.berthVisits.at(this.berthVisitNumber());
    switch (this.mode()) {
      case CargoComponentMode.DISCHARGE:
        return berthVisit?.dischargedGoods;
      case CargoComponentMode.LOADING:
      default:
        return berthVisit?.loadedGoods;
    }
  }, {equal: (before, after) => _.isEqual(before, after)});

  protected readonly selectedCargoTypes = computed(
    () => this.goods()?.map(goodsGroup => goodsGroup.cargoType)
      .filter(cargoType => cargoType != null) as string[] || [],
    {equal: (before, after) => _.isEqual(before, after)}
  );

  protected readonly availableCargoTypes = computed(
    () => this.cargoTypes().filter(cargoType => !this.selectedCargoTypes().includes(cargoType))
  );

  protected readonly hasDeadweightError = computed(() => {
    if (this.formValues() && this.berthVisitGroup) {
      const hasVisitWeightError = this.form.control.errors?.deadweight || false;
      const hasBerthVisitWeightError = this.berthVisitGroup.errors?.deadweight || false;
      return hasVisitWeightError || hasBerthVisitWeightError;
    }
    return false;
  });

  ngOnInit() {
    const berthVisitsArray = this.form.control.get('berthVisits') as FormArray;
    this.berthVisitGroup = berthVisitsArray.at(this.berthVisitNumber()) as FormControl;
    switch (this.mode()) {
      case CargoComponentMode.DISCHARGE:
        this.goodsGroups = this.berthVisitGroup.get(
          'dischargedGoods'
        ) as unknown as FormArray<FormGroup<HarbourDuesGoodsItemForm>>;
        break;
      case CargoComponentMode.LOADING:
      default:
        this.goodsGroups = this.berthVisitGroup.get(
          'loadedGoods'
        ) as unknown as FormArray<FormGroup<HarbourDuesGoodsItemForm>>;
        break;
    }

    this.goodsGroups?.controls.forEach(group => {
      group.controls.shipToShip.setValue(false);
    });

    this.addDefaultGoodsItem();
  }

  private addDefaultGoodsItem() {
    if (this.goodsGroups && this.goodsGroups?.length < 1) {
      this.addGoods();
    }
  }

  protected addGoods(): void {
    const group = this.harbourDuesService.newGoodsItem();
    group.controls.shipToShip.setValue(false);

    this.goodsGroups?.push(group);
  }

  protected removeGoods(index: number): void {
    this.goodsGroups?.removeAt(index);
  }

  protected formatCargoType = (cargoTypeKey: string): string => {
    const cargoTypes = this.lookupCargoTypes() || [];
    const cargoType = cargoTypes.find(
      cargoType => cargoType.cargoType === cargoTypeKey
    );
    if (!cargoType) {
      return cargoTypeKey;
    }
    return cargoType?.description;
  }
}

export enum CargoComponentMode {
  LOADING = "LOADING",
  DISCHARGE = "DISCHARGE"
}
